import { useState } from 'react';
import StatusInfoTab from 'components/tabs/StatusInfoTab';
import useDatasetList, {
    EnvironmentDatasetList,
} from '../hooks/useDatasetList';
import { NTable } from '@innersource-nn/novoui-react';
import PaginationComponent from 'components/pager/Pagination';
import { DatasetSortField, SearchFilter } from 'types/Filters';
import SearchBar from 'components/search/SearchBar';
import TableActionButton from 'components/buttons/TableActionButton';
import { TableSortStateType, TableSortState } from '@novonordisk/novoui-vuejs';
import { FormatDate } from 'components/dates/FormatDate';

const tableHeaders = [
    { header: 'Name', key: 'label', isSortable: true },
    { header: 'Bucket', key: 'S3BucketName' },
    { header: 'Admin Group', key: 'SamlAdminGroupName' },
    { header: 'AWS accounts', key: 'AwsAccountId' },
    { header: 'Created by', key: 'owner' },
    {
        header: 'Created on',
        key: 'created',
        isSortable: true,
        initialSortState: TableSortState.desc,
    },
    { header: '', key: 'action' },
];

const DatasetList = () => {
    const [filter, setFilter] = useState<SearchFilter>({
        page: 1,
        pageSize: 10,
        term: '',
        sort: [{ field: 'created', direction: 'desc' }],
    });

    const { rows, error, loading, items, showSearchbar } =
        useDatasetList(filter);

    const sortItems = (newSortMap: Map<string, TableSortStateType>) => {
        for (const [key, value] of newSortMap) {
            const field = key as DatasetSortField;
            if (value === TableSortState.asc || value === TableSortState.desc) {
                setFilter({
                    ...filter,
                    sort: [{ field, direction: value }],
                });
                break; // NTable only allows to sort one field at the time
            }
        }
    };

    const adjustItems = (items: EnvironmentDatasetList[]) =>
        items.map((item) => ({
            ...item,
            owner: item.owner.split('@')[0].toUpperCase(),
            created: FormatDate(item.created),
            AwsAccountId: `${item.AwsAccountId} - ${item.datasetStage}`,
            action: <TableActionButton path={`/dataset/${item.datasetUri}`} />,
        }));

    const showTable = !error && (loading || showSearchbar);

    return (
        <div>
            {showTable && (
                <>
                    <div className="mb-16">
                        <SearchBar
                            className="w-full"
                            placeholder="Search for datasets in this environment..."
                            term={filter.term ?? ''}
                            onTermChange={(e: string) =>
                                setFilter({ ...filter, term: e, page: 1 })
                            }
                        />
                    </div>
                    <div>
                        <NTable
                            loadXMoreRowsAtATime={loading ? 5 : undefined}
                            headers={tableHeaders}
                            columnBorder={true}
                            items={adjustItems(!loading ? rows : [])}
                            rowKey="datasetUri"
                            sorting={sortItems}
                        />
                        <PaginationComponent
                            items={items}
                            filter={filter}
                            setFilter={setFilter}
                        />
                    </div>
                </>
            )}

            {!showTable && (
                <StatusInfoTab
                    child="datasets"
                    parent="environment"
                    error={error}
                />
            )}
        </div>
    );
};

export default DatasetList;
